import { useMemo, useContext, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import ZoneContext from '../context/ZoneContext';
import { useGruser } from './useGruser';
import { useZone } from './useZone';
import { useModule } from './useModule';
import { usePrivileges } from './usePrivileges';
import { addModule as addModuleAction, moveModule as moveModuleAction, removeModule as removeModuleAction } from '../redux/actions/layout';
// Decide which layout actions are available for the specified module (move up,
// move down, remove, add) in the current zone, and define actions dispatchers
// for all those actions, if the privileges allow
// In da-user-profiles, this was done automatically by the Zone component, but
// we want to keep things decoupled, so this hook can be used in various
// projects and allow for customization
export const useModuleLayoutActions = (moduleId)=>{
    var _module_privileges, _module_privileges1;
    const { gruser } = useGruser();
    const module = useModule(moduleId);
    const moduleName = module === null || module === void 0 ? void 0 : module.name;
    const dispatch = useDispatch();
    const zone = useContext(ZoneContext);
    const zoneName = zone === null || zone === void 0 ? void 0 : zone.name;
    const { installedModulesIds = [] } = useZone(zoneName) || {};
    const position = installedModulesIds.indexOf(moduleId);
    const isFirst = position === 0;
    const isLast = position === installedModulesIds.length - 1;
    const { canMoveModule, canAddModule } = usePrivileges();
    const canAddModuleColumn = isLast && (zone === null || zone === void 0 ? void 0 : zone.direction) === 'row' && canAddModule;
    const canRemoveModule = (module === null || module === void 0 ? void 0 : (_module_privileges = module.privileges) === null || _module_privileges === void 0 ? void 0 : _module_privileges.delete) === true;
    const canEditModule = (module === null || module === void 0 ? void 0 : (_module_privileges1 = module.privileges) === null || _module_privileges1 === void 0 ? void 0 : _module_privileges1.edit) === true;
    const canMoveModuleUp = !isFirst && canMoveModule;
    const canMoveModuleDown = !isLast && canMoveModule;
    const dispatchAddModuleColumn = useCallback(()=>dispatch(addModuleAction(gruser, {
            zoneName,
            moduleName,
            position: position + 1
        })), [
        dispatch,
        gruser,
        zoneName,
        moduleName,
        position
    ]);
    const dispatchRemoveModule = useCallback(()=>dispatch(removeModuleAction(gruser, {
            zoneName,
            moduleId
        })), [
        zoneName,
        gruser,
        moduleId,
        dispatch
    ]);
    const dispatchMoveModuleUp = useCallback(()=>dispatch(moveModuleAction(gruser, {
            zoneName,
            moduleId,
            position: position - 1
        })), [
        zoneName,
        gruser,
        moduleId,
        position,
        dispatch
    ]);
    const dispatchMoveModuleDown = useCallback(()=>dispatch(moveModuleAction(gruser, {
            zoneName,
            moduleId,
            position: position + 1
        })), [
        gruser,
        zoneName,
        moduleId,
        position,
        dispatch
    ]);
    const removeModule = useMemo(()=>canRemoveModule ? dispatchRemoveModule : undefined, [
        canRemoveModule,
        dispatchRemoveModule
    ]);
    const moveModuleUp = useMemo(()=>canMoveModuleUp ? dispatchMoveModuleUp : undefined, [
        canMoveModuleUp,
        dispatchMoveModuleUp
    ]);
    const moveModuleDown = useMemo(()=>canMoveModuleDown ? dispatchMoveModuleDown : undefined, [
        canMoveModuleDown,
        dispatchMoveModuleDown
    ]);
    const addModuleColumn = useMemo(()=>canAddModuleColumn ? dispatchAddModuleColumn : undefined, [
        isLast,
        canAddModule,
        dispatchAddModuleColumn
    ]);
    return {
        moduleId,
        canAddModuleColumn,
        canEditModule,
        canMoveModuleUp,
        canMoveModuleDown,
        canRemoveModule,
        addModuleColumn,
        removeModule,
        moveModuleUp,
        moveModuleDown
    };
};
export default useModuleLayoutActions;
